import { useState } from "react";
import PageBanner from "../components/PageBanner";
import Seo from "../components/Seo";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, SectionLabel } from "../components/Motion";
import { useApp } from "../AppContext";
import { IMAGES, GURUPURNIMA_2026_PHOTOS, SHRI_GURUPRATIPADA_2026_PHOTOS } from "../content";
import { X, Download, Link as LinkIcon, MessageCircle, Facebook, Sparkles } from "lucide-react";
import { toast } from "sonner";

// All festival albums — newest first. Add more here as future events are captured.
const ALBUMS = [
  {
    id: "gurupurnima-2026",
    title_mr: "गुरुपौर्णिमा उत्सव २०२६",
    title_en: "Guru Pornima Festival 2026",
    date_mr: "आषाढ शुद्ध पौर्णिमा, २०२६",
    date_en: "Ashadha Shuddha Purnima, 2026",
    photos: GURUPURNIMA_2026_PHOTOS,
  },
  {
    id: "gurupratipada-2026",
    title_mr: "श्री गुरुप्रतिपदा उत्सव २०२६",
    title_en: "Shri Guru Pratipada Festival 2026",
    date_mr: "माघ वद्य प्रतिपदा, २०२६ (श्री स्वामींचा महासमाधी दिन)",
    date_en: "Magha Vadya Pratipada, 2026 (Mahasamadhi Day)",
    photos: SHRI_GURUPRATIPADA_2026_PHOTOS,
  },
];

export default function Events() {
  const { lang } = useApp();
  const [active, setActive] = useState(null); // { album, idx }
  const current = active ? ALBUMS.find((a) => a.id === active.album).photos[active.idx] : null;
  const step = (d) => {
    if (!active) return;
    const album = ALBUMS.find((a) => a.id === active.album);
    const n = album.photos.length;
    setActive({ ...active, idx: (active.idx + d + n) % n });
  };
  const total = active ? ALBUMS.find((a) => a.id === active.album).photos.length : 0;

  return (
    <div>
      <Seo title="Events & Festival Albums" description="Photo albums from festivals and events at Shri Datta Kshetra Audumbar — Guru Pornima, Datta Jayanti, Guru Pratipada, and more. Fresh albums added each month." />
      <PageBanner
        label={lang === "mr" ? "उत्सव व कार्यक्रम" : "Events"}
        titleObj={{ title_mr: "उत्सव व कार्यक्रम", title_en: "Events & Festival Albums" }}
        subObj={{ intro_mr: "मठातील उत्सव, महोत्सव व सेवा कार्यक्रमांचे छायाचित्र संग्रह — दर महिन्याला नविन अल्बम जोडले जातात.", intro_en: "Photo albums from festivals, utsavs and seva programmes at the Math — a fresh album is added each month." }}
        image={IMAGES.courtyardTree}
      />

      <section className="py-16 lg:py-24 max-w-[1400px] mx-auto px-4 lg:px-8">
        {ALBUMS.map((album) => (
          <div key={album.id} className="mb-20" data-testid={`album-${album.id}`}>
            <Reveal>
              <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
                <div>
                  <SectionLabel>{lang === "mr" ? album.date_mr : album.date_en}</SectionLabel>
                  <h2 className="font-serif-head text-3xl lg:text-5xl text-earth-900 font-bold leading-tight">
                    {lang === "mr" ? album.title_mr : album.title_en}
                  </h2>
                  <p className="mt-2 text-earth-700 text-lg">
                    {lang === "mr" ? `${album.photos.length} छायाचित्रे — क्लिक करून मोठे पहा` : `${album.photos.length} photos — click any to view larger`}
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full bg-saffron-500/10 text-saffron-700 border border-saffron-500/30 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider">
                  <Sparkles size={14} /> {lang === "mr" ? "नविन अल्बम" : "New Album"}
                </span>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {album.photos.map((p, i) => (
                <Reveal key={i} delay={(i % 4) * 0.05}>
                  <button
                    data-testid={`${album.id}-photo-${i}`}
                    onClick={() => setActive({ album: album.id, idx: i })}
                    className="block w-full rounded-xl overflow-hidden border border-earth-900/10 aspect-square bg-earth-900/5 group focus:outline-none focus:ring-2 focus:ring-saffron-500"
                  >
                    <img src={p.url} alt={lang === "mr" ? p.mr : p.en} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {current && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)} data-testid="events-lightbox"
            className="fixed inset-0 z-[70] bg-earth-900/95 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="absolute top-4 right-4 flex items-center gap-3">
              <span className="text-sand-100/70 text-sm font-semibold">{active.idx + 1} / {total}</span>
              <button onClick={(e) => { e.stopPropagation(); setActive(null); }} data-testid="events-close"
                className="rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"><X size={20} /></button>
            </div>
            <button onClick={(e) => { e.stopPropagation(); step(-1); }} data-testid="events-prev"
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white w-11 h-11 flex items-center justify-center text-2xl">‹</button>
            <button onClick={(e) => { e.stopPropagation(); step(1); }} data-testid="events-next"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 text-white w-11 h-11 flex items-center justify-center text-2xl">›</button>
            <motion.figure initial={{ scale: 0.94 }} animate={{ scale: 1 }} className="max-w-5xl" onClick={(e) => e.stopPropagation()}>
              <img src={current.url} alt={lang === "mr" ? current.mr : current.en} className="max-h-[72vh] w-auto rounded-lg mx-auto" />
              <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
                <a href={`https://wa.me/?text=${encodeURIComponent(`${lang === "mr" ? current.mr : current.en} — ${current.url}`)}`} target="_blank" rel="noreferrer"
                  data-testid="events-share-whatsapp" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium">
                  <MessageCircle size={16} /> {lang === "mr" ? "व्हॉट्सअ‍ॅप" : "WhatsApp"}
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(current.url)}`} target="_blank" rel="noreferrer"
                  data-testid="events-share-facebook" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium">
                  <Facebook size={16} /> Facebook
                </a>
                <button onClick={() => { navigator.clipboard?.writeText(current.url); toast.success(lang === "mr" ? "लिंक कॉपी झाली" : "Link copied"); }}
                  data-testid="events-share-copy" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-medium">
                  <LinkIcon size={16} /> {lang === "mr" ? "लिंक कॉपी" : "Copy link"}
                </button>
                <a href={current.url} download target="_blank" rel="noreferrer"
                  data-testid="events-share-download" className="inline-flex items-center gap-2 rounded-full bg-saffron-500 hover:bg-saffron-600 text-white px-4 py-2 text-sm font-medium">
                  <Download size={16} /> {lang === "mr" ? "डाउनलोड" : "Download"}
                </a>
              </div>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
